import React from "react";

import { formatAmount } from "../../utils/FormatPerson";
import { formatOrdinalActivityDate } from "./userActivityDateFormat";
import {
  cryptoWithdrawalCount,
  formatSummaryNgn,
  formatSummaryUsdt,
  mergeCurrencyBreakdown,
  nairaActivityCount,
  sortByCount,
  type UserBalanceSummary,
} from "./userBalanceSummaryUtils";

type LabeledValue = { label: string; value: string };

function ValueChip({ label, value }: LabeledValue) {
  return (
    <div className="flex min-w-[72px] flex-col rounded-md border border-[#2C363E] bg-[#0D1115] px-2.5 py-1.5">
      <span className="text-[9px] font-semibold uppercase tracking-wider text-[#617889]">
        {label}
      </span>
      <span className="mt-0.5 text-[12px] font-semibold text-[#DEE1E6]">
        {value}
      </span>
    </div>
  );
}

function ValueChipGroup({ values }: { values: LabeledValue[] }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {values.map((item) => (
        <ValueChip key={item.label} label={item.label} value={item.value} />
      ))}
    </div>
  );
}

function StatCard({
  label,
  count,
  metrics,
  accent,
}: {
  label: string;
  count: string | number;
  metrics?: LabeledValue[];
  accent: string;
}) {
  return (
    <div className="min-h-[100px] rounded-lg border border-[#2C363E] bg-transparent px-5 py-4">
      <h4 className={`text-[13px] font-bold ${accent}`}>{label}</h4>
      <div className="mt-2 flex items-end gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-[#617889]">
          Count
        </span>
        <p className="font-sora text-[22px] font-bold leading-none text-[#BEE1FB] lgss:text-[18px]">
          {count}
        </p>
      </div>
      {metrics && metrics.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {metrics.map((item) => (
            <ValueChip key={item.label} label={item.label} value={item.value} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DetailSection({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[#2C363E] bg-[#13181B] p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h5 className="text-[13px] font-semibold text-white">{title}</h5>
        {count != null ? (
          <span className="rounded-md border border-[#2C363E] bg-[#0D1115] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#617889]">
            Count <span className="text-[#BEE1FB]">{formatAmount(count)}</span>
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[#2C363E]/60 py-2.5 last:border-b-0">
      <span className="shrink-0 text-[12px] text-[#869AA9]">{label}</span>
      <span className="text-right text-[12px] font-medium text-[#DEE1E6]">
        {value}
      </span>
    </div>
  );
}

function MetricRowValues({
  label,
  values,
}: {
  label: string;
  values: LabeledValue[];
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[#2C363E]/60 py-3 last:border-b-0">
      <span className="shrink-0 pt-1 text-[12px] font-medium text-[#869AA9]">
        {label}
      </span>
      <ValueChipGroup values={values} />
    </div>
  );
}

function BreakdownTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  if (rows.length === 0) {
    return <p className="text-[12px] text-[#617889]">No breakdown data</p>;
  }

  return (
    <div className="mt-2 overflow-x-auto">
      <table className="w-full min-w-[280px] text-left">
        <thead>
          <tr className="border-b border-[#2C363E]">
            {headers.map((header) => (
              <th
                key={header}
                className="pb-2 pr-3 text-[10px] font-semibold uppercase tracking-wide text-[#617889] last:pr-0"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row[0]}-${index}`}
              className="border-b border-[#2C363E]/40 last:border-b-0"
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={`${cell}-${cellIndex}`}
                  className="py-2 pr-3 text-[12px] text-[#C6D0D7] last:pr-0"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type UserBalanceSummaryPanelProps = {
  summary: UserBalanceSummary | null | undefined;
};

export function UserBalanceSummaryPanel({
  summary,
}: UserBalanceSummaryPanelProps) {
  if (!summary) return null;

  const depositCurrencies = mergeCurrencyBreakdown(
    summary.cryptoDeposits?.byCurrency
  );
  const sellDepositCurrencies = mergeCurrencyBreakdown(
    summary.sellDeposits?.byCurrency
  );
  const withdrawalCurrencies = mergeCurrencyBreakdown(
    summary.cryptoWithdrawals?.regular?.byCurrency
  );
  const swapPairs = sortByCount(summary.cryptoSwaps?.byPair).map((pair) => ({
    ...pair,
    label: `${pair.from ?? "—"} → ${pair.to ?? "—"}`,
  }));
  const giftCards = sortByCount(summary.giftCards?.byCard);
  const payoutSources = sortByCount(summary.payouts?.bySource);
  const payoutStatuses = sortByCount(summary.payouts?.byStatus);
  const utilityTypes = sortByCount(summary.cryptoWithdrawals?.utility?.byType);
  const nairaUtilityTypes = sortByCount(summary.naira?.utility?.byType);

  const withdrawalCount = cryptoWithdrawalCount(summary.cryptoWithdrawals);
  const nairaCount = nairaActivityCount(summary.naira);

  const withdrawalMetrics: LabeledValue[] = [];
  if (summary.cryptoWithdrawals?.regular?.count) {
    withdrawalMetrics.push({
      label: "Regular",
      value: formatAmount(summary.cryptoWithdrawals.regular.count),
    });
  }
  if (summary.cryptoWithdrawals?.utility?.count) {
    withdrawalMetrics.push({
      label: "Utility",
      value: formatAmount(summary.cryptoWithdrawals.utility.count),
    });
  }

  return (
    <div className="mt-4 space-y-6 pb-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8 ">
        <StatCard
          label="Total transactions"
          count={summary.totalTransactions ?? 0}
          accent="text-[#0A6AAF]"
        />
        <StatCard
          label="Sells"
          count={summary.sells?.count ?? 0}
          metrics={[
            {
              label: "USDT value",
              value: formatSummaryUsdt(summary.sells?.totalUSDT),
            },
            {
              label: "NGN value",
              value: formatSummaryNgn(summary.sells?.totalNGN),
            },
          ]}
          accent="text-[#F28A0F]"
        />
        <StatCard
          label="Sell deposits"
          count={summary.sellDeposits?.count ?? 0}
          metrics={[
            {
              label: "USDT value",
              value: formatSummaryUsdt(summary.sellDeposits?.totalUSDT),
            },
            {
              label: "NGN value",
              value: formatSummaryNgn(summary.sellDeposits?.totalNGN),
            },
          ]}
          accent="text-[#2DD4BF]"
        />
        <StatCard
          label="Crypto deposits"
          count={summary.cryptoDeposits?.count ?? 0}
          accent="text-[#22C55E]"
        />
        <StatCard
          label="Crypto withdrawals"
          count={withdrawalCount}
          metrics={withdrawalMetrics.length > 0 ? withdrawalMetrics : undefined}
          accent="text-[#0FBCF2]"
        />
        <StatCard
          label="Swaps"
          count={summary.cryptoSwaps?.count ?? 0}
          accent="text-[#9747FF]"
        />
        <StatCard
          label="Gift cards"
          count={summary.giftCards?.count ?? 0}
          accent="text-[#EC4899]"
        />
        <StatCard
          label="Payouts"
          count={summary.payouts?.count ?? 0}
          metrics={[
            {
              label: "NGN value",
              value: formatSummaryNgn(summary.payouts?.totalNGN),
            },
          ]}
          accent="text-[#EAB308]"
        />
      </div>

      {summary.firstTransaction || summary.lastTransaction ? (
        <p className="text-[12px] text-[#617889]">
          {summary.firstTransaction ? (
            <>
              First transaction{" "}
              <span className="text-[#869AA9]">
                {formatOrdinalActivityDate(summary.firstTransaction)}
              </span>
            </>
          ) : null}
          {summary.firstTransaction && summary.lastTransaction ? " · " : null}
          {summary.lastTransaction ? (
            <>
              Last transaction{" "}
              <span className="text-[#869AA9]">
                {formatOrdinalActivityDate(summary.lastTransaction)}
              </span>
            </>
          ) : null}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <DetailSection title="Sells" count={summary.sells?.count}>
          <MetricRowValues
            label="Totals"
            values={[
              {
                label: "USDT value",
                value: formatSummaryUsdt(summary.sells?.totalUSDT),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(summary.sells?.totalNGN),
              },
            ]}
          />
          <BreakdownTable
            headers={["Asset", "Count", "USDT value", "NGN value"]}
            rows={(summary.sells?.byAsset ?? []).map((item) => [
              item.asset ?? "—",
              formatAmount(item.count ?? 0),
              formatSummaryUsdt(item.totalUSDT),
              formatSummaryNgn(item.totalNGN),
            ])}
          />
        </DetailSection>

        <DetailSection
          title="Sell deposits"
          count={summary.sellDeposits?.count}
        >
          <MetricRowValues
            label="Totals"
            values={[
              {
                label: "USDT value",
                value: formatSummaryUsdt(summary.sellDeposits?.totalUSDT),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(summary.sellDeposits?.totalNGN),
              },
            ]}
          />
          <BreakdownTable
            headers={["Currency", "Count", "USDT value", "NGN value"]}
            rows={sellDepositCurrencies.map((item) => [
              item.currency,
              formatAmount(item.count),
              formatSummaryUsdt(item.totalUSDT),
              formatSummaryNgn(item.totalNGN),
            ])}
          />
        </DetailSection>

        <DetailSection
          title="Crypto deposits"
          count={summary.cryptoDeposits?.count}
        >
          <BreakdownTable
            headers={["Currency", "Count", "Total amount"]}
            rows={depositCurrencies.map((item) => [
              item.currency,
              formatAmount(item.count),
              `${formatAmount(item.totalAmount)} ${item.currency}`,
            ])}
          />
        </DetailSection>

        <DetailSection title="Crypto withdrawals" count={withdrawalCount}>
          <MetricRowValues
            label="Regular withdrawals"
            values={[
              {
                label: "Count",
                value: formatAmount(
                  summary.cryptoWithdrawals?.regular?.count ?? 0
                ),
              },
              {
                label: "USDT value",
                value: formatSummaryUsdt(
                  summary.cryptoWithdrawals?.regular?.totalUSDT
                ),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(
                  summary.cryptoWithdrawals?.regular?.totalNGN
                ),
              },
            ]}
          />
          <MetricRowValues
            label="Utility payments"
            values={[
              {
                label: "Count",
                value: formatAmount(
                  summary.cryptoWithdrawals?.utility?.count ?? 0
                ),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(
                  summary.cryptoWithdrawals?.utility?.totalNGN
                ),
              },
            ]}
          />
          <BreakdownTable
            headers={["Currency", "Count", "USDT value", "NGN value"]}
            rows={withdrawalCurrencies.map((item) => [
              item.currency,
              formatAmount(item.count),
              formatSummaryUsdt(item.totalUSDT),
              formatSummaryNgn(item.totalNGN),
            ])}
          />
          {utilityTypes.length > 0 ? (
            <BreakdownTable
              headers={["Utility type", "Count", "NGN value"]}
              rows={utilityTypes.map((item) => [
                (item.type ?? "—").replace(/_/g, " "),
                formatAmount(item.count ?? 0),
                formatSummaryNgn(item.totalNGN),
              ])}
            />
          ) : null}
        </DetailSection>

        <DetailSection title="Swaps" count={summary.cryptoSwaps?.count}>
          <BreakdownTable
            headers={["Pair", "Count"]}
            rows={swapPairs.map((pair) => [
              pair.label,
              formatAmount(pair.count ?? 0),
            ])}
          />
        </DetailSection>

        <DetailSection title="Naira activity" count={nairaCount}>
          <MetricRowValues
            label="Deposits"
            values={[
              {
                label: "Count",
                value: formatAmount(summary.naira?.deposits?.count ?? 0),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(summary.naira?.deposits?.totalNGN),
              },
            ]}
          />
          <MetricRowValues
            label="Withdrawals"
            values={[
              {
                label: "Count",
                value: formatAmount(summary.naira?.withdrawals?.count ?? 0),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(summary.naira?.withdrawals?.totalNGN),
              },
            ]}
          />
          <MetricRowValues
            label="Swaps"
            values={[
              {
                label: "Count",
                value: formatAmount(summary.naira?.swaps?.count ?? 0),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(summary.naira?.swaps?.totalNGN),
              },
            ]}
          />
          <MetricRowValues
            label="Utility"
            values={[
              {
                label: "Count",
                value: formatAmount(summary.naira?.utility?.count ?? 0),
              },
              {
                label: "NGN value",
                value: formatSummaryNgn(summary.naira?.utility?.totalNGN),
              },
            ]}
          />
          {nairaUtilityTypes.length > 0 ? (
            <BreakdownTable
              headers={["Type", "Count", "NGN value"]}
              rows={nairaUtilityTypes.map((item) => [
                (item.type ?? "—").replace(/_/g, " "),
                formatAmount(item.count ?? 0),
                formatSummaryNgn(item.totalNGN),
              ])}
            />
          ) : null}
        </DetailSection>

        <DetailSection title="Gift cards" count={summary.giftCards?.count}>
          <BreakdownTable
            headers={["Card", "Count"]}
            rows={giftCards.map((item) => [
              (item.card ?? "—").trim(),
              formatAmount(item.count ?? 0),
            ])}
          />
        </DetailSection>

        <DetailSection title="Payouts" count={summary.payouts?.count}>
          <MetricRow
            label="Total NGN value"
            value={formatSummaryNgn(summary.payouts?.totalNGN)}
          />
          <BreakdownTable
            headers={["Source", "Count", "NGN value"]}
            rows={payoutSources.map((item) => [
              item.source ?? "Unknown",
              formatAmount(item.count ?? 0),
              formatSummaryNgn(item.totalNGN),
            ])}
          />
          <BreakdownTable
            headers={["Status", "Count", "NGN value"]}
            rows={payoutStatuses.map((item) => [
              item.status ?? "—",
              formatAmount(item.count ?? 0),
              formatSummaryNgn(item.totalNGN),
            ])}
          />
        </DetailSection>
      </div>
    </div>
  );
}
